import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Key, Plus, Copy, Trash2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { apiAccessMessage, canUseApiKeys, canUseLiveMode } from '../../lib/accountPermissions';

export default function ApiKeys() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [form, setForm] = useState({ name: '', mode: 'test' });
  const [creating, setCreating] = useState(false);
  const isAllowed = canUseApiKeys(user);
  const permissionMessage = apiAccessMessage(user);
  const liveModeAllowed = canUseLiveMode(user);

  const fetchKeys = useCallback(() => {
    if (!isAllowed) {
      setLoading(false);
      return;
    }
    api.get('/api-keys').then(r => { setKeys(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [isAllowed]);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const r = await api.post('/api-keys', form);
      setNewKey(r.data);
      toast.success('API key created!');
      fetchKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create key');
    } finally {
      setCreating(false);
    }
  };

  const deleteKey = async (id) => {
    if (!window.confirm('Delete this API key?')) return;
    try {
      await api.delete(`/api-keys/${id}`);
      toast.success('Key deleted');
      fetchKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleKey = async (id) => {
    try {
      await api.patch(`/api-keys/${id}/toggle`);
      fetchKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>API Keys</h1>
          <p className="text-slate-500 text-sm mt-0.5">Manage your API keys for website integration</p>
        </div>
        <Button
          onClick={() => {
            if (!isAllowed) {
              navigate('/dashboard/verification');
              return;
            }
            setShowCreate(true);
            setNewKey(null);
            setForm({ name: '', mode: 'test' });
          }}
          className="bg-emerald-700 hover:bg-emerald-800 text-white"
          data-testid="create-api-key-btn"
        >
          <Plus size={16} className="mr-1.5" /> Create Key
        </Button>
      </div>

      {!isAllowed && (
        <div className="p-4 rounded-xl border border-amber-200 bg-amber-50 flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className="text-amber-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-900">{permissionMessage}</p>
              <p className="text-xs text-amber-800 mt-0.5">Complete business verification and admin approval to unlock developer tools.</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard/verification')} className="shrink-0">
            Go to Verification
          </Button>
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) setNewKey(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{newKey ? 'API Key Created' : 'Create API Key'}</DialogTitle>
          </DialogHeader>
          {newKey ? (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                Save your secret key now. It will not be shown again.
              </div>
              <div>
                <Label className="text-xs text-slate-500">Public Key</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="text-xs bg-slate-50 p-2 rounded flex-1 overflow-x-auto" data-testid="new-public-key">{newKey.public_key}</code>
                  <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(newKey.public_key); toast.success('Copied!'); }}>
                    <Copy size={14} />
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Secret Key</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="text-xs bg-red-50 p-2 rounded flex-1 overflow-x-auto text-red-700" data-testid="new-secret-key">{newKey.secret_key}</code>
                  <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(newKey.secret_key); toast.success('Copied!'); }}>
                    <Copy size={14} />
                  </Button>
                </div>
              </div>
              <Button onClick={() => { setShowCreate(false); setNewKey(null); }} className="w-full" data-testid="done-btn">Done</Button>
            </div>
          ) : (
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label>Key Name</Label>
                <Input data-testid="api-key-name-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="My Website" className="mt-1" required />
              </div>
              <div>
                <Label>Mode</Label>
                <Select value={form.mode} onValueChange={v => setForm(p => ({ ...p, mode: v }))}>
                  <SelectTrigger className="mt-1" data-testid="api-key-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="test">Test Mode</SelectItem>
                    {liveModeAllowed && <SelectItem value="live">Live Mode</SelectItem>}
                  </SelectContent>
                </Select>
                {!liveModeAllowed && <p className="text-xs text-slate-500 mt-1">Live mode is disabled for this account.</p>}
              </div>
              <Button type="submit" data-testid="create-key-submit-btn" disabled={creating} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white">
                {creating ? 'Creating...' : 'Create API Key'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>
          ) : keys.length === 0 ? (
            <div className="py-12 text-center">
              <Key size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No API keys yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Name</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Public Key</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Mode</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Status</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Created</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {keys.map(k => (
                    <TableRow key={k.id} className="hover:bg-slate-50/50" data-testid={`api-key-row-${k.id}`}>
                      <TableCell className="text-sm font-medium text-slate-900">{k.name}</TableCell>
                      <TableCell><code className="text-xs bg-slate-50 px-1.5 py-0.5 rounded">{k.public_key?.substring(0, 24)}...</code></TableCell>
                      <TableCell><span className={`text-xs px-2 py-0.5 rounded-full ${k.mode === 'live' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>{k.mode}</span></TableCell>
                      <TableCell>
                        <button onClick={() => toggleKey(k.id)} className={`text-xs px-2 py-0.5 rounded-full cursor-pointer ${k.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`} data-testid={`toggle-key-${k.id}`}>
                          {k.status}
                        </button>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{new Date(k.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => deleteKey(k.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-key-${k.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
